/**
 * <ShiftDefinitionValidationBadge /> — M3X.ATTENDANCE F.2.
 *
 * Calls the validate-definition endpoint for a shift; renders an
 * emerald "Mutaba'aa OK" badge or a rose veto banner. Also surfaces
 * crosses_midnight + overlaps_night_window flags inline so HR sees
 * Article 108 / 159 routing implications at edit time.
 */

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { api } from "../../lib/api";

export function ShiftDefinitionValidationBadge({
  shiftId,
}: {
  shiftId: string;
}) {
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: ["shift-validate-definition", shiftId],
    queryFn: () => api.validateShiftDefinition(shiftId),
    enabled: Boolean(shiftId),
    retry: false,
  });

  if (q.isLoading) return null;

  if (q.error) {
    // 422 returns the Mutaba'aa veto message
    return (
      <div className="rounded-md border border-rose-300 bg-rose-50 p-3 text-xs text-rose-900">
        ✦ {String((q.error as Error).message)}
      </div>
    );
  }

  if (!q.data) return null;

  return (
    <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3 text-xs text-emerald-900">
      ✓{" "}
      {t(
        "shift_validation.ok",
        "Mutaba'aa: shift definition passes Article 98 / 101 / 104 floors.",
      )}
      <div className="mt-1 flex gap-3 text-[10px] text-text-tertiary">
        <span>
          {t("shift_validation.crosses_midnight", "Crosses midnight")}:{" "}
          <span className="font-mono">
            {q.data.crosses_midnight ? "YES" : "no"}
          </span>
        </span>
        <span>
          {t("shift_validation.overlaps_night", "Overlaps Article 108 night window")}:{" "}
          <span className="font-mono">
            {q.data.overlaps_night_window ? "YES (women + juvenile routing)" : "no"}
          </span>
        </span>
      </div>
    </div>
  );
}

export default ShiftDefinitionValidationBadge;
